import React from "react";
import { createPortal } from "react-dom";
import useEscapeToClose from "../../hooks/useEscapeToClose";
import "./ConfirmDialog.css";

/**
 * Replaces the native browser `confirm()` with a polished modal that matches
 * the rest of Chariot's UI. Use this for any "are you sure?" prompt — destructive
 * actions especially (delete, void, archive).
 *
 * Props:
 *   open       — boolean, controls visibility
 *   title      — short headline (default: "Are you sure?")
 *   message    — body explanation (required)
 *   confirmText — label on the primary button (default: "Confirm")
 *   cancelText  — label on the secondary button (default: "Cancel")
 *   variant    — "danger" (red, default for destructive) | "primary" (blue)
 *   busy       — disables buttons + shows a spinner on confirm
 *   input      — optional { label, placeholder, required, maxLength }: a text
 *                answer (a reason, say), shown as a box under the message;
 *                with `required`, Confirm waits until it has one
 *   value / onValueChange — that answer (useConfirm keeps it)
 *   altText / onAlt — an optional third choice, at the left (e.g. "Don't
 *                save" beside Keep editing and Save)
 *   onConfirm  — called when user clicks confirm
 *   onCancel   — called when user clicks cancel, ESC, or backdrop
 *
 * Example:
 *   const [confirm, setConfirm] = useState(null);
 *
 *   <button onClick={() => setConfirm({ kind: "delete" })}>Delete</button>
 *
 *   <ConfirmDialog
 *     open={!!confirm}
 *     title="Delete order?"
 *     message="This permanently removes the order and its history."
 *     confirmText="Delete"
 *     variant="danger"
 *     busy={deleting}
 *     onConfirm={async () => { await doDelete(); setConfirm(null); }}
 *     onCancel={() => setConfirm(null)}
 *   />
 */
const ConfirmDialog = ({
  open,
  title = "Are you sure?",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  busy = false,
  input = null,
  value = "",
  onValueChange,
  altText = null,
  onAlt,
  onConfirm,
  onCancel,
}) => {
  // ESC closes the dialog when not in a busy state — same convention as our
  // other modals. Putting the hook above the early return keeps hooks order
  // stable across renders.
  useEscapeToClose(open && !busy, onCancel);

  if (!open) return null;

  // Portal into document.body so the dialog escapes any ancestor stacking
  // context (transforms, scroll containers, filters, etc.) that would
  // otherwise trap its z-index and let unrelated UI bleed through.
  return createPortal(
    <div
      className="confirm-dialog-overlay"
      onClick={() => !busy && onCancel && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        className="confirm-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-dialog-title" className="confirm-dialog-title">
          {title}
        </h2>
        <p className="confirm-dialog-message">{message}</p>
        {input && (
          <label className="confirm-dialog-field">
            {input.label}
            <textarea
              className="confirm-dialog-input"
              value={value}
              placeholder={input.placeholder || ""}
              maxLength={input.maxLength || undefined}
              onChange={(e) => onValueChange && onValueChange(e.target.value)}
              autoFocus
            />
          </label>
        )}
        <div className="confirm-dialog-actions">
          {altText && (
            <button
              type="button"
              className="confirm-dialog-btn confirm-dialog-btn-cancel confirm-dialog-btn-alt"
              onClick={onAlt}
              disabled={busy}
            >
              {altText}
            </button>
          )}
          <button
            type="button"
            className="confirm-dialog-btn confirm-dialog-btn-cancel"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`confirm-dialog-btn confirm-dialog-btn-${variant}`}
            onClick={onConfirm}
            disabled={busy || (input?.required && !String(value || "").trim())}
          >
            {busy ? "Working..." : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default React.memo(ConfirmDialog);
